import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { get, post, put } from "../api";
import { SEVERITY_CLASS, STATUS_LABEL } from "../api";
import type { ActionDetailResponse, ActionStatus, Severity } from "../api";
import { StatusPill } from "../TrackedList";
import { Time } from "../Time";

const SEVERITIES: Severity[] = ["critical", "high", "medium", "low"];
/** The states anybody in scope may move an item to. Closing is separate --
    it is the assignee's call or an admin's, and the server decides. */
const WORKING: ActionStatus[] = ["open", "in_progress", "blocked"];

export default function ActionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ActionDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    void get<ActionDetailResponse>(`/api/actions/${id}`)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [id]);

  useEffect(load, [load]);

  /** Every mutation reloads rather than patching local state: the server
      also writes history entries, and reconstructing those here would be a
      second implementation of the same rules. */
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) return <div className="wrap"><div className="error">{error}</div></div>;
  if (!data) return <div className="empty">Loading…</div>;

  const { item, activity, assignable, can_close } = data;

  return (
    <div className="wrap">
      <p className="sub">
        <Link to="/actions">← Action items</Link> ·{" "}
        <Link to={`/c/${item.customer}`}>{item.customer_display_name}</Link>
      </p>

      <div className="titlerow">
        <h1 style={{ margin: 0 }}>{item.title}</h1>
        <StatusPill status={item.status} />
        <span className={`badge ${SEVERITY_CLASS[item.severity]}`}>{item.severity}</span>
      </div>

      <p className="sub">
        Raised by {item.created_by_name ?? "somebody"}{" "}
        <Time value={item.created_at} mode="relative" />
        {item.source_report_id && (
          <>
            {" "}·{" "}
            <Link to={`/c/${item.customer}/report?r=${item.source_report_id}`}>
              from the report that suggested it
            </Link>
          </>
        )}
      </p>

      {error && <div className="error">{error}</div>}

      {!item.is_open && (
        <div className="banner resolved">
          {STATUS_LABEL[item.status]} by {item.resolved_by_name ?? "somebody"}{" "}
          <Time value={item.resolved_at} mode="relative" />.
          {item.resolution && <div style={{ marginTop: 6 }}>{item.resolution}</div>}
        </div>
      )}

      <div className="split">
        <div>
          <Editor
            item={item}
            assignable={assignable}
            busy={busy}
            onSave={(body) => act(() => put(`/api/actions/${item.id}`, body))}
          />

          <h3>Activity</h3>
          <ul className="feed">
            {activity.map((e) => (
              <li key={e.id} className={e.kind}>
                <div className="who">
                  <strong>{e.author_name ?? e.author_email ?? "somebody"}</strong>{" "}
                  <Time value={e.created_at} mode="relative" />
                </div>
                <div className="body">{e.body}</div>
              </li>
            ))}
            {activity.length === 0 && <li className="event">Nothing yet.</li>}
          </ul>

          <CommentBox
            busy={busy}
            onSend={(body) => act(() => post(`/api/actions/${item.id}/comments`, { body }))}
          />
        </div>

        <aside className="side">
          <h3>Move it</h3>
          <div className="stack">
            {WORKING.filter((s) => s !== item.status).map((s) => (
              <button
                key={s}
                disabled={busy}
                onClick={() => act(() => post(`/api/actions/${item.id}/status`, { status: s }))}
              >
                {item.status === "resolved" || item.status === "cancelled"
                  ? `Reopen as ${STATUS_LABEL[s].toLowerCase()}`
                  : `Mark ${STATUS_LABEL[s].toLowerCase()}`}
              </button>
            ))}
          </div>

          {item.is_open && (
            <Closer
              busy={busy}
              canClose={can_close}
              assignee={item.assignee_name}
              onClose={(status, note) =>
                act(() => post(`/api/actions/${item.id}/status`, { status, note }))
              }
            />
          )}

          <h3>Where it came from</h3>
          <p className="dim">
            {item.source_action_id
              ? "Promoted from a report suggestion. The agent may keep mentioning it; this row is what anybody actually owns."
              : "Raised directly, not from a report."}
          </p>
          <button onClick={() => navigate(`/c/${item.customer}`)}>
            Open {item.customer_display_name} →
          </button>
        </aside>
      </div>
    </div>
  );
}

// --- Editing ------------------------------------------------------------

function Editor({
  item,
  assignable,
  busy,
  onSave,
}: {
  item: ActionDetailResponse["item"];
  assignable: ActionDetailResponse["assignable"];
  busy: boolean;
  onSave: (body: unknown) => void;
}) {
  // Re-seeded whenever the server's copy changes, so a save elsewhere is
  // not silently overwritten by a stale form.
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description ?? "");
  const [severity, setSeverity] = useState<Severity>(item.severity);
  const [assignee, setAssignee] = useState(item.assignee_user_id ?? "");
  const [eta, setEta] = useState(item.eta ?? "");

  useEffect(() => {
    setTitle(item.title);
    setDescription(item.description ?? "");
    setSeverity(item.severity);
    setAssignee(item.assignee_user_id ?? "");
    setEta(item.eta ?? "");
  }, [item]);

  const dirty =
    title !== item.title ||
    description !== (item.description ?? "") ||
    severity !== item.severity ||
    assignee !== (item.assignee_user_id ?? "") ||
    eta !== (item.eta ?? "");

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          title,
          description: description || null,
          severity,
          assignee_user_id: assignee || null,
          eta: eta || null,
        });
      }}
    >
      <div className="field">
        <label htmlFor="t">Title</label>
        <input id="t" value={title} onChange={(e) => setTitle(e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor="d">Description</label>
        <textarea
          id="d"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="row">
        <div className="field">
          <label htmlFor="sev">Severity</label>
          <select
            id="sev"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as Severity)}
          >
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="as">Assignee</label>
          <select id="as" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">Nobody yet</option>
            {assignable.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          {/* A date input, not a datetime: "by Thursday" is a calendar
              commitment with no clock time to be wrong about elsewhere. */}
          <label htmlFor="eta">ETA (optional)</label>
          <input id="eta" type="date" value={eta} onChange={(e) => setEta(e.target.value)} />
        </div>
        <div className="field tight">
          <button className="primary" type="submit" disabled={busy || !dirty}>
            Save
          </button>
        </div>
      </div>
    </form>
  );
}

// --- Closing ------------------------------------------------------------

function Closer({
  busy,
  canClose,
  assignee,
  onClose,
}: {
  busy: boolean;
  canClose: boolean;
  assignee: string | null;
  onClose: (status: ActionStatus, note: string) => void;
}) {
  const [note, setNote] = useState("");

  if (!canClose) {
    return (
      <>
        <h3>Close it</h3>
        <p className="dim">
          {assignee
            ? `Only ${assignee} or an admin can close this.`
            : "Only the assignee or an admin can close this — assign it first."}
        </p>
      </>
    );
  }

  return (
    <>
      <h3>Close it</h3>
      <div className="field">
        <label htmlFor="note">What happened</label>
        <textarea
          id="note"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional, but it is what the next person reads."
        />
      </div>
      <div className="stack">
        <button className="primary" disabled={busy} onClick={() => onClose("resolved", note)}>
          Resolve
        </button>
        {/* Distinct from resolving on purpose: "we did it" and "it stopped
            mattering" are very different account history. */}
        <button disabled={busy} onClick={() => onClose("cancelled", note)}>
          Cancel it
        </button>
      </div>
    </>
  );
}

function CommentBox({ busy, onSend }: { busy: boolean; onSend: (body: string) => void }) {
  const [body, setBody] = useState("");

  return (
    <form
      className="commentbox"
      onSubmit={(e) => {
        e.preventDefault();
        if (!body.trim()) return;
        onSend(body);
        setBody("");
      }}
    >
      <textarea
        rows={3}
        value={body}
        placeholder="Add a comment…"
        onChange={(e) => setBody(e.target.value)}
      />
      <button className="primary" type="submit" disabled={busy || !body.trim()}>
        Comment
      </button>
    </form>
  );
}
